import { useCallback, useEffect, useRef, useState } from 'react'
import { showError } from 'utils/snackbar'
import { log } from 'utils/logger'

// How long a "copied" confirmation stays on screen before the control goes back to its idle label.
const COPIED_LABEL_MS = 2000

interface Options {
    /** Shown when the browser refuses the clipboard, so a failed copy is never mistaken for a done one. */
    errorMessage?: string
    onCopied?: () => void
}

interface Result {
    /** The key last copied — for the control that owns it to show its own confirmation. */
    copiedKey: string | null
    /** Copies `text`, remembering `key` as copied. A key is only needed when one control copies several
     *  values. Resolves to whether the write actually landed — a refusal is reported to the user here and
     *  never thrown, so a caller that acts on success must read this rather than assume it. */
    copy: (text: string | undefined, key?: string, options?: CopyOptions) => Promise<boolean>
}

interface CopyOptions {
    /** The same content as markup, for apps that paste rich text; plain-text targets still get `text`. */
    html?: string
}

const writeToClipboard = async (text: string, html?: string): Promise<void> => {
    if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({ 'text/plain': new Blob([text], { type: 'text/plain' }), 'text/html': new Blob([html], { type: 'text/html' }) }),
            ])
            return
        } catch (error) {
            // Some browsers refuse rich-text writes (or the html type) while allowing plain text: the text
            // still has to land, so fall back to it rather than reporting the copy as failed.
            log.w('Rich clipboard write refused, copying plain text instead', { error })
        }
    }
    await navigator.clipboard.writeText(text)
}

/**
 * Copy-to-clipboard with the confirmation state and its reset timer, shared so every control that
 * offers a "copy this command" button behaves the same — including reporting a refused clipboard
 * instead of leaving the dispatcher believing they have the value.
 */
export const useCopyToClipboard = ({ errorMessage = 'Could not copy to the clipboard', onCopied }: Options = {}): Result => {
    const [copiedKey, setCopiedKey] = useState<string | null>(null)
    // The control can unmount mid-countdown — a truck whose drivers are all connected, a closed panel.
    const resetRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
        () => () => {
            if (resetRef.current) clearTimeout(resetRef.current)
        },
        []
    )

    const onCopiedRef = useRef(onCopied)
    onCopiedRef.current = onCopied

    /** Resolves to whether the text actually reached the clipboard. Callers that only show a checkmark can
     *  keep ignoring it; callers that do something ELSE on success must not treat "it returned" as "it
     *  worked", because a refused write is reported here and never thrown. */
    const copy = useCallback(
        async (text: string | undefined, key = 'default', options?: CopyOptions): Promise<boolean> => {
            if (!text) return false
            try {
                await writeToClipboard(text, options?.html)
                setCopiedKey(key)
                onCopiedRef.current?.()
                if (resetRef.current) clearTimeout(resetRef.current)
                resetRef.current = setTimeout(() => setCopiedKey(null), COPIED_LABEL_MS)
                return true
            } catch (err) {
                log.e('[clipboard] copy failed', err)
                showError(errorMessage)
                return false
            }
        },
        [errorMessage]
    )

    return { copiedKey, copy }
}
